import { useTranslation } from "react-i18next";
import React, { memo, Suspense, useCallback, useEffect, useRef, useState } from "react";
import {
	ActionTypes,
	CollabCardRenderingFromType,
	FeatureKeysType,
	ICollabCardProps,
	IResponse,
	ModuleTypes,
	storyType,
	SupportedLanguageType,
	userAccessType,
} from "../../models/interfaces";
import { Avatar, Button, Card, message, Meta, Paragraph, PlusCircleFilled, Popconfirm, Tag, Tooltip, Typography } from "src/assets/imports/antdComponents";
import { useNavigate } from "react-router-dom";
import { getInitials, getStudyTypeDisplayName, transformDateTimeCapitalize } from "../../transformer";
import type { StudyTypeIconDescriptor } from "../../transformer";
import StudyTypeIcon from "../StudyTypeIcon";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedStudy, updateStudyStar } from "../../states/studiesSlice";
import { editStudyStar } from "../../serviceHelper";
import { truncateText } from "src/pages/ManageStudies/transformer";
import { useFeatures } from "src/config/featureManager";
import Folder from "public/icon/Folder.svg";
import "./style.css";
import { useActionTracking } from "src/components/UserMonitoring/useActionTracking";
const { Text } = Typography;
const AddCollaboratorModal = React.lazy(() => import("../AddCollaborator"));

const CollabCard: React.FC<ICollabCardProps> = ({ payload, activeStudyId, isSelected, onSelect, onEditStudy, renderedFrom, showButtonHighlight = false }) => {
	const { t } = useTranslation();
	const { isFeatureAvailable } = useFeatures();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { metaDetails, allStudyTypes } = useSelector((state: any) => state.studiesSlice);
	const [isAddCollabModalOpen, setIsAddCollabModalOpen] = useState(false);
	const [collabModalKey, setCollabModalKey] = useState(0);
	const [isDeleting, setIsDeleting] = useState(false);
	const [starred, setStarred] = useState(payload?.is_star ?? false);
	const [starLoading, setStarLoading] = useState<boolean>(false);
	const [tooltipsOpen, setTooltipsOpen] = useState(false);

	const isStudyUpsert = isFeatureAvailable(FeatureKeysType.StudyUpsert);
	const isMultiLingualSupportEnabled = isFeatureAvailable(FeatureKeysType.MultiLingualSupport);
	const isFolderEnabled = isFeatureAvailable(FeatureKeysType.EnableFolder);
	const titleRef = useRef<HTMLDivElement>(null);
	const cardRef = useRef<HTMLDivElement>(null);
	const hasAccess = payload?.user_access_type === userAccessType.edit || payload?.user_access_type === userAccessType.view;
	const hasDeleteAccess =
		payload?.collaborators?.find((collaborator) => collaborator?.collaborator_id?.toLowerCase() === metaDetails?.id?.toLowerCase())?.user_access_type === userAccessType.edit;

	const maxVisibleTags = 3;
	const visibleTags = payload?.custom_tags && payload?.custom_tags.slice(0, maxVisibleTags);
	const extraTagsCount = payload?.custom_tags && payload?.custom_tags?.length - maxVisibleTags;
	const isActiveStudy = activeStudyId === payload?.study_id;

	const { trackAction } = useActionTracking(ModuleTypes.Studies);

	useEffect(() => {
		if (isSelected && cardRef.current) {
			cardRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
		}
	}, [isSelected]);

	// Auto-show both tooltips simultaneously when highlight is active
	useEffect(() => {
		if (showButtonHighlight) {
			// Show both tooltips after a short delay
			setTimeout(() => {
				setTooltipsOpen(true);
			}, 600);

			// Hide both tooltips
			setTimeout(() => {
				setTooltipsOpen(false);
			}, 3100);
		}
	}, [showButtonHighlight]);

	const handleClick = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onEditStudy();
		},
		[onEditStudy],
	);

	const handleManageClick = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			const data = {
				study_id: payload?.study_id,
				study_name: payload?.study_name,
				study_tags: payload?.custom_tags,
				study_collaborators: payload?.collaborators,
				insights_id: payload?.active_insights_id,
				access_type: payload?.user_access_type,
				created_by: payload?.version_history?.created_by,
				study_type: payload?.study_type,
				insights_available: payload?.insights_available,
				is_date_mapped: payload?.is_date_mapped ?? false,
				study_language: payload?.study_language ?? { name: "English", code: SupportedLanguageType.English },
				quant_tabs: payload?.quant_tabs,
				last_updated_at: payload?.version_history?.modified_dt,
				refresh_insights_id: payload?.refresh_insights_id,
			};
			dispatch(setSelectedStudy(data));
			navigate("/manage-studies");
		},
		[payload, dispatch, navigate],
	);

	const handleInsightsClick = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			const data = {
				study_id: payload?.study_id,
				study_name: payload?.study_name,
				study_tags: payload?.custom_tags,
				study_collaborators: payload?.collaborators,
				insights_id: payload?.active_insights_id,
				access_type: payload?.user_access_type,
				created_by: payload?.version_history?.created_by,
				study_type: payload?.study_type,
				insights_available: payload?.insights_available,
				is_date_mapped: payload?.is_date_mapped ?? false,
				study_language: payload?.study_language ?? { name: "English", code: SupportedLanguageType.English },
				quant_tabs: payload?.quant_tabs,
				last_updated_at: payload?.version_history?.modified_dt,
				refresh_insights_id: payload?.refresh_insights_id,
			};
			dispatch(setSelectedStudy(data));
			trackAction(ActionTypes.INSIGHTS_VIEWED, {
				feature: true,
				action_name: getStudyTypeDisplayName(payload?.study_type, allStudyTypes),
				type: undefined,
			});
			navigate("/insights", { state: { previousPath: window.location.pathname, pathHistory: [window.location.pathname] } });
		},
		[payload, dispatch, navigate],
	);

	const showAddCollabModal = (e: React.MouseEvent) => {
		e.stopPropagation();
		setCollabModalKey((prevKey) => prevKey + 1);
		setIsAddCollabModalOpen(true);
	};

	const handleOk = () => {
		setIsAddCollabModalOpen(false);
	};

	const handleCancel = useCallback(() => {
		setIsAddCollabModalOpen(false);
	}, [payload]);

	const handleToggleStar = useCallback(
		async (e) => {
			e.preventDefault();
			e.stopPropagation();
			if (starLoading) return;
			setStarLoading(true);

			try {
				const response: IResponse = await editStudyStar({ entity_id: payload?.study_id, is_star: !starred });
				if (response && response?.message) {
					setStarred((prev) => !prev);
					trackAction(ActionTypes.STARRED_UPDATED, {
						study_id: payload?.study_id,
						study_name: payload?.study_name,
						study_type: payload?.study_type,
						feature: true,
						type: undefined,
					});
					dispatch(updateStudyStar({ study_id: payload?.study_id, star: !starred }));
					message.success(t(response?.message));
				}
			} catch (error) {
				console.error("Error toggling star:", error);
				message.error(t(error));
			} finally {
				setStarLoading(false);
			}
		},
		[starred, starLoading, payload],
	);

	const getStudyTypeIcon = useCallback((studyType: string, allStudyTypes: any[]): StudyTypeIconDescriptor | null => {
		const matchedType = allStudyTypes?.find((type) => type?.label === studyType || type?.key === studyType);
		return matchedType?.icon2 ?? null;
	}, []);

	return (
		<div ref={cardRef}>
			<Card
				className={`custom-study-card collab-card flex flex-col ${isSelected && "cardActive"}`}
				hoverable
				onClick={handleClick}
				title={
					<div className="flex items-center justify-between w-full leading-[1.2]">
						<div className="flex items-center justify-start gap-2 max-w-[75%]">
							<div onClick={handleToggleStar} className="collab-card-star-icon">
								{starred ? (
									<i className="material-symbols-outlined filled-icon text-[--secondary-color-2] collab-card-star-icon-filled text-2xl">star</i>
								) : (
									<i className="material-symbols-outlined text-[--secondary-color-2] text-2xl">star</i>
								)}
							</div>
							<div className="flex flex-col max-w-[90%]">
								<div className="flex items-center gap-2">
									{/* {isFolderEnabled && payload?.folder && payload.folder?.folder_name ? (
										<span className="flex items-center leading-[0px]">
											<Tooltip title={payload.folder.folder_name}>
												<Folder />
											</Tooltip>
											
										</span>
									) : (
										<i className="material-symbols-outlined text-[1.5rem] text-[--secondary-color-opacity-30] flex-shrink-0">local_library</i>
									)} */}
									<StudyTypeIcon descriptor={getStudyTypeIcon(payload?.study_type, allStudyTypes)} className="w-[18px]" />
									<Text ref={titleRef} ellipsis={{ tooltip: true }} className="collab-card-head-title">
										{payload?.study_name}
									</Text>
								</div>
								<span className="custom-type">
									{isMultiLingualSupportEnabled && payload?.study_type === storyType.type1 && (
										<Tooltip
											getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body}
											title={t("Study Language")}
											placement="bottom"
											overlayClassName="tooltip-reduced-padding">
											<Tag className="text-[x-small] text-[--secondary-color-2-darker] border-[--neutral-grey-opacity-15] bg-[--neutral-white] !leading-[14px]">
												{t(payload?.study_language?.name || "English")}
											</Tag>
										</Tooltip>
									)}
									{t(getStudyTypeDisplayName(payload.study_type, allStudyTypes))}
								</span>
							</div>
						</div>
						<div className="flex items-center gap-3">
							{isStudyUpsert && (
								<Tooltip
									getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body}
									title={renderedFrom === CollabCardRenderingFromType.ManageStudies ? t("You are currently viewing manage study page") : t("Manage")}
									open={showButtonHighlight ? tooltipsOpen : undefined}
									placement="topRight"
									onOpenChange={(visible) => {
										if (!showButtonHighlight) {
										}
									}}>
									<Button
										className={`study-card-header-navigation-btn ${showButtonHighlight ? "button-highlight" : ""}`}
										disabled={renderedFrom === CollabCardRenderingFromType.AddStudy || renderedFrom === CollabCardRenderingFromType.ManageStudies}
										type="text"
										onClick={handleManageClick}>
										<i className="material-symbols-outlined">page_info</i>
									</Button>
								</Tooltip>
							)}
							{payload?.study_type !== storyType.type2 && (
								<Tooltip
									getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body}
									title={!payload?.insights_available ? t("Insights are not available") : t("Insights")}
									open={showButtonHighlight ? tooltipsOpen : undefined}
									placement="topLeft">
									<Button
										className={`study-card-header-navigation-btn mr-1 ${showButtonHighlight ? "button-highlight" : ""}`}
										type="text"
										onClick={handleInsightsClick}
										disabled={!payload?.insights_available}>
										<i className="material-symbols-outlined">emoji_objects</i>
									</Button>
								</Tooltip>
							)}
						</div>
					</div>
				}>
				<div className={`custom-description ${isActiveStudy && "cardActiveDes"}`}>
					<Meta
						description={
							<Paragraph
								ellipsis={{
									rows: 2,
									expandable: false,
									tooltip: {
										title: payload?.description,
										placement: "bottom",
										styles: {
											body: {
												maxHeight: "200px",
												maxWidth: "300px",
												overflow: "auto",
											},
										},
									},
								}}
								className="custom-description-text text-base-14">
								{payload?.description}
							</Paragraph>
						}
					/>
				</div>
				<div className="collab-card-custom-tags flex items-center">
					{visibleTags?.map((tag, index) => (
						<Tag key={`${tag}-${index}`} className="collab-card-study-tag mr-2" title={tag}>
							{truncateText(tag, 15)}
						</Tag>
					))}
					{extraTagsCount > 0 && (
						<Tooltip
							getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body}
							overlayClassName="collab-card-tags-tooltip"
							title={
								<div className="flex gap-2 flex-wrap">
									{payload.custom_tags?.slice(maxVisibleTags).map((tag, index) => (
										<Tag key={`${tag}-${index}`} className="collab-card-study-tag m-1" title={tag}>
											{truncateText(tag, 40)}
										</Tag>
									))}
								</div>
							}>
							<Tag className="collab-card-study-tag mr-2">
								+ {extraTagsCount} {t("more")}
							</Tag>
						</Tooltip>
					)}
				</div>
				<div className="absolute bottom-[-4px] left-0 right-0">
					<div className="flex items-center justify-between p-[1.16667rem]">
						<div className="flex items-center h-full">
							<span className="collab-card-custom-author">
								{transformDateTimeCapitalize(payload?.version_history?.modified_by?.full_name, payload?.version_history?.modified_dt, t)}
							</span>
						</div>
						<div className="flex items-center h-full gap-4">
							<div className="flex items-center -space-x-1">
								<Avatar.Group size="small" max={{ count: 4 }} className="mix-blend-normal -z-0">
									{payload?.collaborators?.map((collaborator) => (
										<Tooltip
											getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body}
											key={collaborator.collaborator_id}
											title={collaborator.collaborator_name}>
											<Avatar
												key={collaborator.collaborator_id}
												size="small"
												className={`${collaborator.is_owner ? "owner-avatar" : ""} ${collaborator.is_active === false ? "inactive-avatar" : ""}`}>
												{getInitials(collaborator.collaborator_name)}
											</Avatar>
										</Tooltip>
									))}
								</Avatar.Group>
								<Tooltip
									getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body}
									title={!hasAccess && t("Request exisiting collaborators to add more collaborators")}>
									<PlusCircleFilled
										className={`mix-blend-normal text-[1.4rem] z-0 ${hasAccess ? "collab-card-plus-icon" : "collab-card-plus-icon-disabled"}`}
										onClick={(e) => {
											e.stopPropagation();
											if (hasAccess) {
												showAddCollabModal(e);
											}
										}}
									/>
								</Tooltip>
							</div>
							<Popconfirm
								title={t("Confirm Deletion?")}
								placement="leftBottom"
								// onConfirm={handleDeleteConfirm}
								okText={t("Yes")}
								cancelText={t("No")}
								okButtonProps={{ loading: isDeleting }}
								disabled={!hasDeleteAccess}>
								<Tooltip
									getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body}
									placement="left"
									title={hasDeleteAccess ? "" : t("No access")}>
									<div
										className={`action-button collab-card-action-button flex items-center justify-center ${
											hasDeleteAccess ? "cursor-pointer" : "cursor-not-allowed opacity-50"
										}`}
										onClick={(e) => e.stopPropagation()}>
										<i className="material-symbols-outlined collab-card-action-icon collab-card-delete-icon">delete</i>
									</div>
								</Tooltip>
							</Popconfirm>
						</div>
					</div>
				</div>
			</Card>
			<Suspense fallback={<></>}>
				{isAddCollabModalOpen && (
					<AddCollaboratorModal
						authorUser={payload?.version_history.created_by}
						fullAccessUsers={payload?.collaborators}
						isModalOpen={isAddCollabModalOpen}
						handleOk={handleOk}
						handleCancel={handleCancel}
						key={collabModalKey}
						studyId={payload?.study_id}
						studyName={payload?.study_name}
					/>
				)}
			</Suspense>
		</div>
	);
};

export default memo(CollabCard);
